import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  FileText,
  Briefcase,
  CheckCircle,
  Award,
  Shield,
  Mail,
  Laptop,
  Lock,
  Headphones,
  ClipboardList
} from 'lucide-react';
import { Link } from 'react-router-dom';

const sections = [
  { id: 'overview', title: 'Overview', icon: FileText },
  { id: 'getting-started', title: 'Getting Started', icon: Briefcase },
  { id: 'scoping', title: 'Scoping & Proposals', icon: ClipboardList },
  { id: 'delivery', title: 'Our Delivery Process', icon: CheckCircle },
  { id: 'site-access', title: 'Site Access & Safety', icon: Award },
  { id: 'documentation', title: 'Documentation & Handover', icon: Laptop },
  { id: 'support', title: 'Post-Project Support', icon: Headphones },
  { id: 'privacy', title: 'Data Privacy & NDA', icon: Lock },
  { id: 'contact', title: 'Contact & Escalation', icon: Mail }
];

const updated = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

const ClientServiceGuide = () => {
  return (
    <>
      <Helmet>
        <title>Client Service Guide - Tasks IT Services</title>
        <meta
          name="description"
          content="Everything you need to know about working with Tasks IT Services â€” from engagement and scoping to project delivery, handover, and post-project support."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'TechArticle',
            headline: 'Client Service Guide',
            dateModified: updated,
            about: ['IT project delivery', 'Site access requirements', 'Documentation and handover', 'Post-project support']
          })}
        </script>
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900" />
        <div className="pointer-events-none absolute -top-40 -left-40 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="pointer-events-none absolute -bottom-40 -right-40 w-[32rem] h-[32rem] bg-blue-600/25 rounded-full blur-3xl" />

        {/* Hero */}
        <section className="relative pt-20 pb-12 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl shadow-xl mb-6">
                <FileText className="h-10 w-10" />
              </div>
              <h1 className="text-5xl font-bold mb-4">Client Service Guide</h1>
              <p className="text-blue-100 max-w-2xl mx-auto">
                Everything you need to know about engaging Tasks IT Services â€” from first contact through to post-project support.
              </p>
              <div className="mt-4 text-sm text-blue-200">Last updated: {updated}</div>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="relative pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
            {/* TOC */}
            <aside className="lg:col-span-4 xl:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="lg:sticky lg:top-24"
              >
                <div className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-6 text-white shadow-2xl">
                  <h3 className="text-lg font-semibold mb-4">Navigation</h3>
                  <nav className="space-y-2">
                    {sections.map(s => (
                      <a
                        key={s.id}
                        href={`#${s.id}`}
                        className="block px-3 py-2 rounded-lg text-sm text-blue-100 hover:bg-white/10 hover:text-white transition"
                      >
                        {s.title}
                      </a>
                    ))}
                  </nav>
                </div>
              </motion.div>
            </aside>

            {/* Main */}
            <div className="lg:col-span-8 xl:col-span-9 space-y-8">
              {sections.map(({ id, title, icon: Icon }, idx) => (
                <motion.section
                  key={id}
                  id={id}
                  initial={{ opacity: 0, y: 26 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-120px' }}
                  transition={{ duration: 0.5, delay: 0.03 * idx }}
                  className="relative group"
                >
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/25 via-cyan-500/10 to-purple-500/25 blur-xl opacity-40 group-hover:opacity-75 transition" />
                  <div className="relative backdrop-blur-xl bg-white/10 border border-white/15 rounded-3xl p-6 md:p-8 text-white shadow-2xl">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h2 className="text-2xl font-bold">{title}</h2>
                    </div>

                    {id === 'overview' && (
                      <p className="text-blue-100 leading-relaxed">
                        This guide explains how to engage Tasks IT Services, what to expect at each stage of a project,
                        how we handle handover and documentation, and how to access support after a project completes.
                        Use it as a reference throughout your engagement with our team.
                      </p>
                    )}

                    {id === 'getting-started' && (
                      <div className="space-y-4 text-blue-100">
                        <p>Starting a project with Tasks IT Services is straightforward:</p>
                        <ol className="list-decimal pl-5 space-y-2">
                          <li>Reach out via our <Link to="/contact" className="text-cyan-300 underline hover:text-cyan-200">Contact page</Link> or email us directly.</li>
                          <li>Describe your IT challenge â€” what service you need and your target timeline.</li>
                          <li>We schedule a free discovery call (30â€“60 minutes) to understand your environment and requirements.</li>
                          <li>We prepare and send a tailored project proposal within 3â€“5 business days.</li>
                        </ol>
                        <div className="mt-4">
                          <Link
                            to="/contact"
                            className="inline-block px-5 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-medium hover:opacity-90"
                          >
                            Contact Us to Get Started
                          </Link>
                        </div>
                      </div>
                    )}

                    {id === 'scoping' && (
                      <div className="space-y-4 text-blue-100">
                        <h3 className="font-semibold text-white">What we need from you</h3>
                        <ul className="list-disc pl-5 space-y-2">
                          <li>Site floor plans or network diagrams (if available â€” not required).</li>
                          <li>Number of locations, floors, or server rooms involved.</li>
                          <li>Existing hardware inventory or equipment list (if applicable).</li>
                          <li>Your project timeline and any critical deadlines.</li>
                        </ul>
                        <h3 className="font-semibold text-white pt-2">What we deliver in the proposal</h3>
                        <ul className="list-disc pl-5 space-y-2">
                          <li>Clear scope of work with defined deliverables.</li>
                          <li>Estimated project timeline with key milestones.</li>
                          <li>Fixed-price or time-and-materials pricing breakdown.</li>
                          <li>Team composition and certifications relevant to your project.</li>
                        </ul>
                      </div>
                    )}

                    {id === 'delivery' && (
                      <div className="space-y-4 text-blue-100">
                        <p>All Tasks IT Services projects follow our proven 4-step delivery framework:</p>
                        <ol className="list-decimal pl-5 space-y-3">
                          <li><span className="text-white font-semibold">Scope & Assess</span> â€” Site survey, environment review, and requirements confirmation.</li>
                          <li><span className="text-white font-semibold">Design & Plan</span> â€” Solution design, equipment procurement, and project scheduling.</li>
                          <li><span className="text-white font-semibold">Deploy & Implement</span> â€” On-site installation, configuration, and integration by certified engineers.</li>
                          <li><span className="text-white font-semibold">Test & Hand Over</span> â€” End-to-end testing, documentation delivery, and client sign-off.</li>
                        </ol>
                        <p className="text-sm opacity-80">
                          Your assigned project manager provides regular status updates throughout each phase.
                        </p>
                      </div>
                    )}

                    {id === 'site-access' && (
                      <div className="space-y-4 text-blue-100">
                        <p>For on-site work, please arrange the following before our engineers arrive:</p>
                        <ul className="list-disc pl-5 space-y-2">
                          <li>Building or server room access passes for the project duration.</li>
                          <li>A site contact who can escort engineers and approve access.</li>
                          <li>Power and network patching access in relevant areas.</li>
                          <li>Loading dock or goods-in access for equipment delivery.</li>
                        </ul>
                        <div className="rounded-xl bg-white/10 border border-white/15 p-4 text-sm">
                          <span className="text-cyan-300 font-semibold">Safety note:</span> Our engineers follow all site safety rules and wear appropriate PPE. Please notify us of any site-specific health and safety requirements in advance.
                        </div>
                      </div>
                    )}

                    {id === 'documentation' && (
                      <div className="space-y-4 text-blue-100">
                        <p>At project completion, every client receives a full handover package including:</p>
                        <ul className="list-disc pl-5 space-y-2">
                          <li>As-built network or installation diagrams.</li>
                          <li>Device configuration backups and inventory spreadsheet.</li>
                          <li>Test results and sign-off checklist.</li>
                          <li>Vendor warranty certificates and support contact details.</li>
                          <li>User guides or admin manuals (where applicable).</li>
                        </ul>
                        <p className="text-sm opacity-80">
                          Documentation is delivered digitally within 5 business days of project completion.
                        </p>
                      </div>
                    )}

                    {id === 'support' && (
                      <div className="space-y-4 text-blue-100">
                        <p>We offer several post-project support options to keep your infrastructure running smoothly:</p>
                        <ul className="list-disc pl-5 space-y-2">
                          <li><span className="text-white font-semibold">30-day warranty period</span> â€” Included with every project at no charge.</li>
                          <li><span className="text-white font-semibold">Ad-hoc support</span> â€” Call out a certified engineer as needed, billed per engagement.</li>
                          <li><span className="text-white font-semibold">Managed Services</span> â€” Ongoing monitoring, maintenance, and helpdesk support via SLA contract.</li>
                        </ul>
                        <div className="mt-4">
                          <Link
                            to="/contact"
                            className="inline-block px-5 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-medium hover:opacity-90"
                          >
                            Enquire About Support Options
                          </Link>
                        </div>
                      </div>
                    )}

                    {id === 'privacy' && (
                      <div className="space-y-3 text-blue-100">
                        <p>
                          All project information, network diagrams, and site data shared with us are treated as strictly
                          confidential. We maintain an NDA with every client as a standard part of our engagement.
                        </p>
                        <ul className="list-disc pl-5 space-y-2">
                          <li>Client data is never shared with third parties without explicit consent.</li>
                          <li>Configuration files and diagrams are stored securely and deleted after project close.</li>
                          <li>NDAs are available on request prior to the discovery call.</li>
                        </ul>
                        <p className="text-sm">
                          See our{' '}
                          <Link to="/privacy" className="text-cyan-300 underline hover:text-cyan-200">
                            Privacy Policy
                          </Link>{' '}
                          for full details.
                        </p>
                      </div>
                    )}

                    {id === 'contact' && (
                      <div className="space-y-4 text-blue-100">
                        <p>For general enquiries, new project requests, or ongoing project questions, reach your team via:</p>
                        <ul className="list-disc pl-5 space-y-2">
                          <li><span className="text-white font-semibold">Project Manager</span> â€” Your assigned PM is your first point of contact for active projects.</li>
                          <li><span className="text-white font-semibold">Email</span> â€” <span className="text-cyan-300">GlennRosas@hotmail.com</span> for general enquiries.</li>
                          <li><span className="text-white font-semibold">Phone</span> â€” <span className="text-cyan-300">+81 70-2633-2830</span> during business hours (Monâ€“Fri, 9:00â€“18:00 JST).</li>
                        </ul>
                        <div className="flex flex-wrap gap-3 mt-4">
                          <Link
                            to="/contact"
                            className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-medium hover:opacity-90"
                          >
                            Send a Message
                          </Link>
                        </div>
                        <p className="text-xs opacity-70">
                          Response time: typically within 1 business day.
                        </p>
                      </div>
                    )}
                  </div>
                </motion.section>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center text-blue-200 text-sm pt-4"
              >
                Â© {new Date().getFullYear()} Tasks IT Services â€¢ Enterprise IT Infrastructure Solutions.
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ClientServiceGuide;
